import type { TFile } from "@/api/shared/file";
import type { EDentitionType, ETMJ } from "@/api/models/patient/orthoProposedPlan";
import type { TTeethApprove } from "@/api/models/patient/teeth.ts";
import type { DefaultPaginatedFilters } from "@/api/shared/filters";
import type { TCompositeView, TCompositeViewCreate } from "@/api/models/patient/compositeView";

/**
 * Which arches a treatment covers.
 *
 * Lives here rather than in `useOrthoNeeds` (where it was declared) so schema modules can import it
 * without pulling in the query layer — that composable also builds query keys, and importing it from a
 * plain schema module blew up at module-init. `useOrthoNeeds` re-exports it, so existing imports are
 * unaffected.
 */
export enum EArchesToTreat {
  BOTH = "both",
  UPPER = "upper",
  LOWER = "lower",
}

export enum EConditionsFields {
  OVERBITE = "overbite",
  GROWTH = "growth",
  ORAL_HABITS = "oral_habits",
  OVERJET = "overjet",
  SPACE_MAINTENANCE = "space_maintenance",
  CROWDING = "crowding",
  MINOR_TOOTH_MOVEMENT = "minor_tooth_movement",
  MISSING_TEETH = "missing_teeth",
  OPEN_BITE = "open_bite",
  UNDER_BITE = "underbite",
  PRE_PROSTHETIC_ALIGNMENT = "pre_prosthetic_alignment",
  SPACING = "spacing",
}

export interface TOrthoNeeds {
  id?: string;
  patient: string;
  created_at?: string;
  io_exam_information_doctor: string;
  io_exam_information_doctor_date: string;
  last_check_up_date: string;
  perio_clearance_doctor_name: string;
  perio_clearance_doctor_date: string;
  arches_to_treat: string;
  overbite: boolean;
  growth_skeletal: boolean;
  missing_teeth: boolean;
  oral_habits: boolean;
  overjet: boolean;
  space_maintenance: boolean;
  crowding: boolean;
  minor_movement: boolean;
  underbite: boolean;
  pre_prosthetic_align: boolean;
  spacing: boolean;
  teeth_not_to_be_moved: TTeethApprove;
  teeth_to_be_extracted: TTeethApprove;
  notes: string | null;
  readiness_level: number | null;
  scans: TFile[];
  xrays: TFile[];
  photos: TFile[];
  supporting_documents: TFile[];
  composite_view: TCompositeViewCreate | TCompositeView;
  tmj: ETMJ[];
  tmj_notes: string | null;
  confirmed_at: string | null;
  dentition_type: EDentitionType;
}

export enum TOrthoNeedsMarkType {
  CONFIRM = "confirm",
  APPROVE = "approve",
}

export enum EClearence {
  CLEARED = "cleared",
  NOT_CLEARED = "not_cleared",
}

export interface PriceOption {
  name: string;
  description: string;
  price: number;
  checked: boolean;
  value: string;
}

export interface IOrthoNeedsFilter extends DefaultPaginatedFilters {
  patient?: string;
}
